import { Link } from "react-router-dom";
import { useHosts } from "@/hooks/use-api";

function pluralize(n: number, severity: "stale" | "dead"): string {
  const noun = n === 1 ? "agent" : "agents";
  return `${n} ${noun} ${severity}`;
}

/**
 * Top-bar fleet-health hint (#289). Reads `summary.{dead,stale}` from
 * the shared `useHosts()` query — coalesces network requests with the
 * Hosts page and refreshes together when a revoke invalidates the
 * `["me","hosts"]` key.
 *
 * Hidden during loading/error: this surface is a hint, not a
 * notification. Showing a red chip on every transient API failure
 * would train users to ignore it.
 *
 * Precedence (per #289 contract): dead > stale > nothing.
 * `active` and `never_seen` alone never produce a chip.
 */
export function FreshnessChip({ basePath = "/app" }: { basePath?: string }) {
  const { data, isPending, isError } = useHosts();

  if (isPending || isError || !data) return null;

  const { dead, stale } = data.summary;

  if (dead >= 1) {
    return (
      <Link
        to={`${basePath}/hosts`}
        aria-label={pluralize(dead, "dead")}
        data-testid="freshness-chip-dead"
        className="inline-flex items-center gap-2 rounded-full bg-red-500/10 px-3 py-1 text-xs font-medium text-red-700 ring-1 ring-red-500/30 transition-colors hover:bg-red-500/15 dark:text-red-300"
      >
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full bg-red-500"
        />
        {pluralize(dead, "dead")}
      </Link>
    );
  }

  if (stale >= 1) {
    return (
      <Link
        to={`${basePath}/hosts`}
        aria-label={pluralize(stale, "stale")}
        data-testid="freshness-chip-stale"
        className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-500/30 transition-colors hover:bg-amber-500/15 dark:text-amber-300"
      >
        <span
          aria-hidden
          className="inline-block h-2 w-2 rounded-full bg-amber-500"
        />
        {pluralize(stale, "stale")}
      </Link>
    );
  }

  return null;
}
